/**
 * Releases v2 (design `docs/superpowers/design-reference/music24-releases-v2/`, owner answers
 * 2026-10-07): every catalog song is a release. This file holds the wire types of the release
 * routes and the pure rules that music24-api and the clients share: the setup check, the group of
 * the list (Upcoming, Active, Quiet) and the next step of a row.
 *
 * The file imports only types and the pure `rolloutSchedule.ts`, so every project can use it.
 */
import type { PresaveFanReleaseState } from '../smartlinks/presaveSubscriptions';
import type { LyricSyncState } from './lyricTiming';
import type { ReleaseReelsPlan } from './reelsPlan';
import { scheduleRolloutSteps } from './rolloutSchedule';

/** The days of activity that keep a song in Active (design a1). */
export const RELEASE_ACTIVE_DAYS = 30;

/** "Out in N days" is the next step only for N up to this number (design a1). */
export const RELEASE_SOON_DAYS = 7;

/** Without a view row, "N new" counts what was made in these days (a first visit is not "everything is new"). */
export const RELEASE_FIRST_VISIT_NEW_DAYS = 7;

// ---------------------------------------------------------------- Setup

/** The four setup items of a release (design b1, b3). `presave` counts only while the song is upcoming. */
export type ReleaseSetupKey = 'master' | 'coverAndDate' | 'lyrics' | 'smartLink' | 'presave';

export type ReleaseSetupState = 'ok' | 'missing' | 'working' | 'failed';

/** The section of the Catalog song page that fixes an item (`/app/catalog/song/:uuid?tab=`). */
export type ReleaseSongSection = 'files' | 'details' | 'lyrics' | 'release';

/** Why an item is not ok. The clients turn each code into one sentence. */
export type ReleaseSetupDetail =
  | 'noMaster'
  | 'masterProcessing'
  | 'masterFailed'
  | 'noCover'
  | 'noDate'
  | 'noCoverNoDate'
  | 'noLyrics'
  | 'noTiming'
  | 'timingSyncing'
  | 'timingFailed'
  | 'noSmartLink'
  | 'smartLinkDraft'
  | 'noPresave';

export interface ReleaseSetupItem {
  key: ReleaseSetupKey;
  state: ReleaseSetupState;
  /** The song page section that fixes the item. */
  section: ReleaseSongSection;
  /** Null when the item is ok. */
  detail: ReleaseSetupDetail | null;
}

export interface ReleaseSetup {
  /** True when no item is `missing` or `failed` (a master that is still read counts as done). */
  complete: boolean;
  /** Every item that applies, in the order of the design. */
  items: ReleaseSetupItem[];
  /** The keys that are `missing` or `failed`, in the same order. */
  missing: ReleaseSetupKey[];
}

/** The facts of a catalog row that the setup check reads. */
export interface ReleaseSetupFacts {
  hasMaster: boolean;
  /** The master is stored, but its AAC copy or its analysis still runs. */
  masterProcessing: boolean;
  masterFailed: boolean;
  hasCover: boolean;
  /** `YYYY-MM-DD` or null. */
  releaseDate: string | null;
  hasLyrics: boolean;
  lyricSyncStatus: LyricSyncState;
  /** The SmartLink of the song: published, a draft, or none. */
  smartLinkStatus: 'published' | 'draft' | null;
  hasPresave: boolean;
}

/** True when the release day is today or later. */
export function isUpcomingRelease(releaseDate: string | null, today: string): boolean {
  return releaseDate !== null && releaseDate.slice(0, 10) >= today;
}

function masterItem(facts: ReleaseSetupFacts): ReleaseSetupItem {
  const item = { key: 'master', section: 'files' } as const;
  if (!facts.hasMaster) {
    return { ...item, state: 'missing', detail: 'noMaster' };
  }
  if (facts.masterFailed) {
    return { ...item, state: 'failed', detail: 'masterFailed' };
  }
  if (facts.masterProcessing) {
    return { ...item, state: 'working', detail: 'masterProcessing' };
  }
  return { ...item, state: 'ok', detail: null };
}

function coverAndDateItem(facts: ReleaseSetupFacts): ReleaseSetupItem {
  const item = { key: 'coverAndDate', section: 'details' } as const;
  if (!facts.hasCover && facts.releaseDate === null) {
    return { ...item, state: 'missing', detail: 'noCoverNoDate' };
  }
  if (!facts.hasCover) {
    return { ...item, state: 'missing', detail: 'noCover' };
  }
  if (facts.releaseDate === null) {
    return { ...item, state: 'missing', detail: 'noDate' };
  }
  return { ...item, state: 'ok', detail: null };
}

function lyricsItem(facts: ReleaseSetupFacts): ReleaseSetupItem {
  const item = { key: 'lyrics', section: 'lyrics' } as const;
  if (!facts.hasLyrics) {
    return { ...item, state: 'missing', detail: 'noLyrics' };
  }
  switch (facts.lyricSyncStatus) {
    case 'synced':
      return { ...item, state: 'ok', detail: null };
    case 'syncing':
      return { ...item, state: 'working', detail: 'timingSyncing' };
    case 'failed':
      return { ...item, state: 'failed', detail: 'timingFailed' };
    case 'none':
      return { ...item, state: 'missing', detail: 'noTiming' };
  }
}

function smartLinkItem(facts: ReleaseSetupFacts): ReleaseSetupItem {
  const item = { key: 'smartLink', section: 'release' } as const;
  if (facts.smartLinkStatus === null) {
    return { ...item, state: 'missing', detail: 'noSmartLink' };
  }
  if (facts.smartLinkStatus === 'draft') {
    return { ...item, state: 'missing', detail: 'smartLinkDraft' };
  }
  return { ...item, state: 'ok', detail: null };
}

/**
 * The setup check of one release (design b1, b3): the master, the cover and the release date, the
 * lyrics with their timing, the SmartLink, and the pre-save while the song is upcoming. The setup
 * lives on the Catalog song page; each item names its section there.
 */
export function releaseSetupCheck(facts: ReleaseSetupFacts, today: string): ReleaseSetup {
  const items: ReleaseSetupItem[] = [
    masterItem(facts),
    coverAndDateItem(facts),
    lyricsItem(facts),
    smartLinkItem(facts),
  ];
  if (isUpcomingRelease(facts.releaseDate, today)) {
    items.push({
      key: 'presave',
      section: 'release',
      state: facts.hasPresave ? 'ok' : 'missing',
      detail: facts.hasPresave ? null : 'noPresave',
    });
  }
  const missing = items.filter((item) => item.state === 'missing' || item.state === 'failed').map((item) => item.key);
  return { complete: missing.length === 0, items, missing };
}

// ---------------------------------------------------------------- Group and next step

/** The three groups of the list (design a1). */
export type ReleaseGroup = 'upcoming' | 'active' | 'quiet';

const DAY_MS = 86_400_000;

/** The whole days from `today` (`YYYY-MM-DD`) to the release day; null with no date. */
export function daysToRelease(releaseDate: string | null, today: string): number | null {
  if (releaseDate === null) {
    return null;
  }
  const release = Date.parse(`${releaseDate.slice(0, 10)}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(release) || Number.isNaN(now)) {
    return null;
  }
  return Math.round((release - now) / DAY_MS);
}

/**
 * Upcoming: the release day is today or later. Active: something was made, posted or planned in
 * the last `RELEASE_ACTIVE_DAYS` days. Quiet: the rest. A song with no date goes to Active or
 * Quiet by its activity.
 */
export function releaseGroup(input: {
  releaseDate: string | null;
  today: string;
  lastActivityAt: string | null;
  now: Date;
}): ReleaseGroup {
  if (isUpcomingRelease(input.releaseDate, input.today)) {
    return 'upcoming';
  }
  if (input.lastActivityAt === null) {
    return 'quiet';
  }
  const age = input.now.getTime() - Date.parse(input.lastActivityAt);
  return age <= RELEASE_ACTIVE_DAYS * DAY_MS ? 'active' : 'quiet';
}

/** The one next step of a row (design a1 note: the first match wins). */
export type ReleaseNextStep =
  | { kind: 'setup'; key: ReleaseSetupKey; detail: ReleaseSetupDetail; missing: ReleaseSetupKey[]; tab: 'overview' }
  | { kind: 'failed'; count: number; tab: 'assets' }
  | { kind: 'outToday'; tab: 'plan' }
  | { kind: 'outSoon'; days: number; tab: 'plan' }
  | { kind: 'fillGaps'; count: number; days: string[]; tab: 'plan' }
  | { kind: 'postsThisWeek'; count: number; nextAt: string; nextPlatform: string | null; tab: 'plan' }
  | { kind: 'postsPlanned'; count: number; nextAt: string | null; tab: 'plan' }
  | { kind: 'planRollout'; tab: 'plan' }
  | { kind: 'planPush'; tab: 'plan' }
  | { kind: 'makeSomething'; tab: 'overview' };

export interface ReleaseNextStepInput {
  setup: ReleaseSetup;
  daysToRelease: number | null;
  /** The newest revisions that failed and have no ready revision behind them. */
  failedCount: number;
  /** The cards of the Assets tab (newest revision per type + variant, plus the reel variants). */
  assetCount: number;
  /** The rollout days of the release that have no post (only once a rollout started). */
  gapDays: string[];
  /** The planned posts (draft, scheduled, needs you, publishing). */
  plannedCount: number;
  /** The planned posts in the next 7 days and the first of them. */
  thisWeekCount: number;
  nextPost: { at: string; platform: string | null } | null;
}

/**
 * The next step of a release (design a1): missing setup; a failed asset; out today; out in N days
 * (N ≤ 7); fill the gaps of the rollout; the posts of this week; the posts planned later; plan the
 * rollout (upcoming); plan a push (released or no date); make something for it.
 */
export function releaseNextStep(input: ReleaseNextStepInput): ReleaseNextStep {
  const firstMissing = input.setup.items.find((item) => item.state === 'missing' || item.state === 'failed');
  if (firstMissing !== undefined && firstMissing.detail !== null) {
    return {
      kind: 'setup',
      key: firstMissing.key,
      detail: firstMissing.detail,
      missing: input.setup.missing,
      tab: 'overview',
    };
  }
  if (input.failedCount > 0) {
    return { kind: 'failed', count: input.failedCount, tab: 'assets' };
  }
  const days = input.daysToRelease;
  if (days === 0) {
    return { kind: 'outToday', tab: 'plan' };
  }
  if (days !== null && days > 0 && days <= RELEASE_SOON_DAYS) {
    return { kind: 'outSoon', days, tab: 'plan' };
  }
  if (input.gapDays.length > 0) {
    return { kind: 'fillGaps', count: input.gapDays.length, days: input.gapDays, tab: 'plan' };
  }
  if (input.thisWeekCount > 0 && input.nextPost !== null) {
    return {
      kind: 'postsThisWeek',
      count: input.thisWeekCount,
      nextAt: input.nextPost.at,
      nextPlatform: input.nextPost.platform,
      tab: 'plan',
    };
  }
  if (input.plannedCount > 0) {
    return { kind: 'postsPlanned', count: input.plannedCount, nextAt: input.nextPost?.at ?? null, tab: 'plan' };
  }
  if (input.assetCount === 0) {
    return { kind: 'makeSomething', tab: 'overview' };
  }
  if (days !== null && days > 0) {
    return { kind: 'planRollout', tab: 'plan' };
  }
  return { kind: 'planPush', tab: 'plan' };
}

// ---------------------------------------------------------------- Wire types

/** One row of `GET /accounts/:accountId/releases`. The clients join it with the catalog row by uuid. */
export interface ReleaseListRow {
  catalogItemUuid: string;
  group: ReleaseGroup;
  /** `YYYY-MM-DD` or null. */
  releaseDate: string | null;
  setup: ReleaseSetup;
  assets: {
    /** One per card: the newest revision of each kit type + variant, and each reel variant. */
    count: number;
    /** Cards whose newest revision is newer than this user's last view of the release. */
    newCount: number;
    workingCount: number;
    failedCount: number;
  };
  posts: {
    /** Posted. */
    done: number;
    /** Draft, scheduled, needs you, publishing. */
    planned: number;
    failed: number;
    /** The planned posts in the next 7 days. */
    thisWeek: number;
    next: { at: string; platform: string | null } | null;
  };
  /** The rollout days with no post (only once a release rollout started). */
  gapDays: string[];
  smartLink: { id: number; status: 'published' | 'draft'; clicks: number | null } | null;
  presave: { saves: number; live: boolean } | null;
  /** The newest thing made, posted or planned for the song (ISO), or null. */
  lastActivityAt: string | null;
  /** This user's last view of the Assets tab (ISO), or null. */
  lastViewedAt: string | null;
  nextStep: ReleaseNextStep;
}

export interface GetReleasesResponse {
  /** Upcoming (soonest first), then Active (newest activity first), then Quiet (newest release first). */
  releases: ReleaseListRow[];
}

/** One post of a release (the Overview Done and Planned panels, the Plan tab list). */
export interface ReleasePost {
  uuid: string;
  platform: string;
  status: string;
  /** ISO. */
  scheduledAt: string;
  postedAt: string | null;
  format: string;
  caption: string;
  rolloutKey: string | null;
  /** Why the post belongs to the release. */
  linkedBy: 'campaign' | 'asset' | 'reel' | 'smartLink';
  /** The kit asset revision of the media file, when the file is one. */
  kitAsset: { uuid: string; themeId: string; variant: string; version: number } | null;
  /** The reel of the media file, when the file is one. */
  reel: { uuid: string; variant: string; revision: number } | null;
  externalPostUrl: string | null;
  failReason: string | null;
}

/** `GET /accounts/:accountId/releases/:catalogItemUuid` (Overview). */
export interface ReleaseOverview {
  catalogItemUuid: string;
  setup: ReleaseSetup;
  lastViewedAt: string | null;
  /** Every post of the release that is not canceled, newest `scheduledAt` first. */
  posts: ReleasePost[];
  smartLink: {
    id: number;
    status: 'published' | 'draft';
    clicks: number | null;
    clicksLast7Days: number | null;
  } | null;
  presave: { saves: number; live: boolean; byService: Record<string, number> } | null;
  /** The "Send to N subscribers" state of a song with no pre-save campaign (§8.2); null with a campaign. */
  fanRelease: PresaveFanReleaseState | null;
  /** The artist subscribers who get the next release of this artist. */
  artistSubscriberCount: number;
}

/** `POST /accounts/:accountId/releases/:catalogItemUuid/viewed`. */
export interface MarkReleaseViewedResponse {
  /** The view before this one, or null for a first view. "Just made" counts from it. */
  previousViewedAt: string | null;
  viewedAt: string;
}

/** One rollout day of the Plan tab (`GET /accounts/:accountId/releases/:catalogItemUuid/plan`). */
export interface ReleasePlanDay {
  /** A `RolloutDayKey` (`announce`, `countdown3`, `push2`, …). */
  key: string;
  /**
   * The real offset: days from the release date (release) or from today (push) to `date`. A step
   * whose planned day had passed reads its new day (`scheduleRolloutSteps`: the -14 step of a
   * release in 7 days reads -7).
   */
  offsetDays: number;
  /** The offset of the rollout table. Absent in an older answer. */
  plannedOffsetDays?: number;
  /** `YYYY-MM-DD`: the real day of the step (`scheduleRolloutSteps`). */
  date: string;
  /** The planned day had passed, so the step moved forward (the name stays for older apps). */
  movedToToday: boolean;
  /** A build-up step of a release that passed: it keeps its past day. Absent in an older answer. */
  missed?: boolean;
  isTask: boolean;
  format: 'post' | 'story' | 'reel';
  asset: {
    themeId: string;
    variant: string;
    status: 'ready' | 'working' | 'missing';
    assetUuid: string | null;
    /** Design rollout-plan (2026-10-09). Absent in an older answer. */
    kind?: 'kitAsset' | 'reel' | 'file';
    fileId?: number | null;
    /** The thumbnail of the step: the image itself or the poster of a video. */
    posterUrl?: string | null;
    revision?: number | null;
    /** "Story (Pre-save) r1", "AI reel 15s r2". */
    label?: string | null;
    /** The render percent while the asset is made. */
    making?: number | null;
    /** The AI reel made for this step from the plan. */
    pinned?: boolean;
  } | null;
  /** The channels of the step (stored per step) that take the day. */
  platforms: string[];
  /** Drafts of the day exist on every channel of the step. */
  added: boolean;
  /** The posts of the release with this day's `rolloutKey` (canceled ones left out). */
  posts: ReleasePost[];
  /** True for a post day of today or later with no post at all (a skipped step is never a gap). */
  gap: boolean;
  /** Design rollout-plan: the artist skipped the step (owner decision 9). */
  skipped?: boolean;
  /** Design rollout-plan: the drafts are added when the asset is ready (owner decisions 3, 4). */
  autoDraft?: boolean;
  /**
   * Design rollout-plan a1: one entry per channel of the step, with its time: the time of its post,
   * else the channel slot in the artist's time zone (ISO), and the post on that channel, if any.
   */
  channels?: ReleasePlanChannel[];
}

/** One channel of a rollout step (design rollout-plan a1, b1). */
export interface ReleasePlanChannel {
  platform: string;
  /** What the channel makes of the step: `story`, `post`, `photo` (TikTok), `reel`, `short` (YouTube). */
  format: 'story' | 'post' | 'photo' | 'reel' | 'short';
  /** ISO. */
  at: string;
  postUuid: string | null;
}

/** `GET /accounts/:accountId/releases/:catalogItemUuid/plan?kind&platforms`. */
export interface ReleasePlan {
  kind: 'release' | 'spike' | 'evergreen' | 'reels';
  /** `YYYY-MM-DD` or null. */
  releaseDate: string | null;
  /** True for a release rollout of a song with no release date: `days` is empty (design f3). */
  needsDate: boolean;
  days: ReleasePlanDay[];
  /** The days with `gap`. */
  gapCount: number;
  /** The drafts that one "Add drafts" click makes now (days with a ready file). */
  postCount: number;
  campaignUuid: string | null;
  /** Design rollout-plan: the skipped rollout keys (`release.outNow`). Absent in an older answer. */
  skippedKeys?: string[];
  /** Design rollout-plan: the artist's time zone (IANA), or null when none is stored. */
  timeZone?: string | null;
  /** Design rollout-plan: "Change channels", the default channels of a new step; null = every channel. */
  defaultPlatforms?: string[] | null;
  /**
   * Only with `kind=reels`: the newest AI reels plan of the song (active or not), or null when the
   * song has none. With no active plan `days` is empty.
   */
  reelsPlan?: ReleaseReelsPlan | null;
}

/**
 * The rollout days of a release with no post: the post days of today or later whose `rolloutKey`
 * no post of the release has. Only once a release rollout started (a post with a `release.` key):
 * before that the next step is "Plan the rollout". The days are the real days of the steps (a past
 * step moves forward, `scheduleRolloutSteps`), so the list and the Plan tab name the same days.
 */
export function releaseGapDays(input: {
  days: ReadonlyArray<{ key: string; offsetDays: number; isTask: boolean }>;
  releaseDate: string | null;
  today: string;
  /** The first day a moved step can use (`rolloutEarliestDay`). Absent: `today`. */
  earliestDay?: string;
  /** The `rolloutKey` of every post of the release that is not canceled. */
  postRolloutKeys: ReadonlyArray<string | null>;
  /**
   * The `rolloutKey`s with a post that is more than a draft: those steps never move. Absent: every
   * key of `postRolloutKeys`.
   */
  fixedRolloutKeys?: ReadonlyArray<string | null>;
}): string[] {
  if (input.releaseDate === null) {
    return [];
  }
  const keys = new Set(input.postRolloutKeys.filter((key): key is string => key !== null));
  const started = [...keys].some((key) => key.startsWith('release.'));
  if (!started) {
    return [];
  }
  const fixed =
    input.fixedRolloutKeys === undefined
      ? keys
      : new Set(input.fixedRolloutKeys.filter((key): key is string => key !== null));
  const schedule = scheduleRolloutSteps({
    anchorDay: input.releaseDate,
    earliestDay: input.earliestDay ?? input.today,
    steps: input.days.map((day) => ({
      key: day.key,
      offsetDays: day.offsetDays,
      fixed: fixed.has(`release.${day.key}`),
    })),
  });
  return input.days
    .map((day, index) => ({ day, date: schedule[index]?.date ?? '' }))
    .filter(({ day }) => !day.isTask)
    .filter(({ day, date }) => date >= input.today && !keys.has(`release.${day.key}`))
    .map(({ date }) => date);
}
